import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { formatApiError } from '../utils/errorHandler';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Plus, Search, Edit2, Trash2, FileText, CalendarIcon, Eye, RefreshCw, CheckCircle2, XCircle, Clock, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContractsPage = () => {
  const { t, language } = useLanguage();
  const currency = language === 'tr' ? '₺' : '$';
  const [contracts, setContracts] = useState([]);
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [accrualMap, setAccrualMap] = useState({});
  const [expandedContract, setExpandedContract] = useState(null);
  const [contractAccruals, setContractAccruals] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [formData, setFormData] = useState({
    unit_id: '',
    tenant_id: '',
    start_date: '',
    end_date: '',
    planned_move_out_date: '',
    monthly_rent: '',
    water_fee: '',
    internet_fee: '',
    due_day: '1',
    deposit_exists: false,
    deposit_amount: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contractsRes, unitsRes, tenantsRes, accrualsRes] = await Promise.all([
        axios.get(`${API_URL}/api/contracts`, { withCredentials: true }),
        axios.get(`${API_URL}/api/units`, { withCredentials: true }),
        axios.get(`${API_URL}/api/tenants`, { withCredentials: true }),
        axios.get(`${API_URL}/api/accruals`, { withCredentials: true })
      ]);
      setContracts(contractsRes.data);
      setUnits(unitsRes.data);
      setTenants(tenantsRes.data);
      
      // Build accrual summary per contract
      const map = {};
      for (const a of accrualsRes.data) {
        if (!a.contract_id) continue;
        if (!map[a.contract_id]) map[a.contract_id] = { paid: 0, unpaid: 0, overdue: 0, partial: 0, total_debt: 0 };
        map[a.contract_id][a.status] = (map[a.contract_id][a.status] || 0) + 1;
        if (a.status !== 'paid') map[a.contract_id].total_debt += a.remaining_amount;
      }
      setAccrualMap(map);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        monthly_rent: parseFloat(formData.monthly_rent),
        water_fee: formData.water_fee ? parseFloat(formData.water_fee) : null,
        internet_fee: formData.internet_fee ? parseFloat(formData.internet_fee) : null,
        due_day: parseInt(formData.due_day),
        deposit_amount: formData.deposit_exists && formData.deposit_amount ? parseFloat(formData.deposit_amount) : null
      };

      if (editingContract) {
        await axios.put(`${API_URL}/api/contracts/${editingContract.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/contracts`, payload, { withCredentials: true });
      }
      toast.success(t('saveSuccess'));
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleEdit = (contract) => {
    setEditingContract(contract);
    setFormData({
      unit_id: contract.unit_id || '',
      tenant_id: contract.tenant_id || '',
      start_date: contract.start_date || '',
      end_date: contract.end_date || '',
      planned_move_out_date: contract.planned_move_out_date || '',
      monthly_rent: contract.monthly_rent?.toString() || '',
      water_fee: contract.water_fee?.toString() || '',
      internet_fee: contract.internet_fee?.toString() || '',
      due_day: contract.due_day?.toString() || '1',
      deposit_exists: contract.deposit_exists || false,
      deposit_amount: contract.deposit_amount?.toString() || ''
    });
    setIsModalOpen(true);
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/contracts/${id}`, { status: newStatus }, { withCredentials: true });
      toast.success(t('saveSuccess'));
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/api/contracts/${id}`, { withCredentials: true });
      toast.success(t('deleteSuccess'));
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const resetForm = () => {
    setEditingContract(null);
    setFormData({
      unit_id: '',
      tenant_id: '',
      start_date: '',
      end_date: '',
      planned_move_out_date: '',
      monthly_rent: '',
      water_fee: '',
      internet_fee: '',
      due_day: '1',
      deposit_exists: false,
      deposit_amount: ''
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-50 text-emerald-700 border-emerald-600',
      moved_out: 'bg-blue-50 text-blue-700 border-blue-600',
      terminated: 'bg-red-50 text-red-700 border-red-600',
      cancelled: 'bg-gray-50 text-gray-700 border-gray-600'
    };
    return `px-2 py-1 text-xs font-bold uppercase border ${styles[status] || 'bg-gray-100'}`;
  };

  const getUnitName = (unitId) => units.find(u => u.id === unitId)?.name || '-';
  const getTenantName = (tenantId) => tenants.find(t => t.id === tenantId)?.name || '-';

  const handleGenerateAccruals = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/api/contracts/generate-accruals`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setGenerating(false);
    }
  };

  const toggleContractDetail = async (contractId) => {
    if (expandedContract === contractId) {
      setExpandedContract(null);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/contracts/${contractId}/payment-status`, { withCredentials: true });
      setContractAccruals(response.data.accruals || []);
      setExpandedContract(contractId);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const getPaymentBadge = (summary) => {
    if (!summary) return null;
    if (summary.overdue > 0) return <span className="px-1.5 py-0.5 text-xs font-bold bg-red-100 text-red-700 border border-red-200">{summary.overdue} {language === 'tr' ? 'Gecikmiş' : 'Overdue'}</span>;
    if (summary.unpaid > 0) return <span className="px-1.5 py-0.5 text-xs font-bold bg-gray-100 text-gray-700 border border-gray-200">{summary.unpaid} {language === 'tr' ? 'Bekliyor' : 'Pending'}</span>;
    if (summary.partial > 0) return <span className="px-1.5 py-0.5 text-xs font-bold bg-amber-100 text-amber-700 border border-amber-200">{summary.partial} {language === 'tr' ? 'Kısmi' : 'Partial'}</span>;
    if (summary.paid > 0) return <span className="px-1.5 py-0.5 text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">{language === 'tr' ? 'Güncel' : 'Up to date'}</span>;
    return <span className="text-xs text-gray-400">-</span>;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />;
      case 'partial': return <Clock className="w-3.5 h-3.5 text-amber-500" />;
      case 'overdue': return <XCircle className="w-3.5 h-3.5 text-red-600" />;
      default: return <AlertCircle className="w-3.5 h-3.5 text-gray-400" />;
    }
  };

  const filteredContracts = contracts.filter(c => 
    statusFilter === 'all' || c.status === statusFilter
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contracts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t('contracts')}</h1>
          <p className="text-gray-500 text-sm mt-1">{contracts.length} {t('total')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleGenerateAccruals}
            disabled={generating}
            variant="outline"
            className="rounded-none"
            data-testid="generate-accruals-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
            {t('generateAccruals')}
          </Button>
          <Button 
            onClick={() => { resetForm(); setIsModalOpen(true); }}
            className="bg-black text-white rounded-none hover:bg-gray-800"
            data-testid="add-contract-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('addContract')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 rounded-none" data-testid="contract-status-filter">
            <SelectValue placeholder={t('status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all')}</SelectItem>
            <SelectItem value="active">{t('active')}</SelectItem>
            <SelectItem value="moved_out">{t('movedOut')}</SelectItem>
            <SelectItem value="terminated">{t('terminated')}</SelectItem>
            <SelectItem value="cancelled">{t('cancelled')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Contracts Table */}
      <div className="border border-gray-200 overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr className="bg-gray-50">
              <th>{t('units')}</th>
              <th>{t('tenants')}</th>
              <th>{t('startDate')}</th>
              <th>{t('endDate')}</th>
              <th>{t('monthlyRent')}</th>
              <th>{t('deposit')}</th>
              <th>{t('paymentStatus')}</th>
              <th>{t('status')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredContracts.length > 0 ? (
              filteredContracts.map((contract) => (
                <React.Fragment key={contract.id}>
                  <tr data-testid={`contract-row-${contract.id}`} className={expandedContract === contract.id ? 'bg-gray-50' : ''}>
                    <td className="font-medium">{getUnitName(contract.unit_id)}</td>
                    <td>{getTenantName(contract.tenant_id)}</td>
                    <td>{contract.start_date?.split('T')[0]}</td>
                    <td>{contract.end_date?.split('T')[0]}</td>
                    <td className="font-mono">{currency}{contract.monthly_rent}</td>
                    <td className="font-mono">
                      {contract.deposit_exists ? `${currency}${contract.deposit_amount || 0}` : '-'}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        {getPaymentBadge(accrualMap[contract.id])}
                        {accrualMap[contract.id]?.total_debt > 0 && (
                          <span className="text-xs font-mono text-red-600 ml-1">
                            {currency}{accrualMap[contract.id].total_debt.toLocaleString()}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={getStatusBadge(contract.status)}>
                        {t(contract.status) || contract.status}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button 
                          onClick={() => toggleContractDetail(contract.id)} 
                          className={`p-1 hover:bg-blue-50 ${expandedContract === contract.id ? 'bg-blue-100 text-blue-600' : 'text-blue-500'}`}
                          title={t('paymentStatus')}
                          data-testid={`view-payments-${contract.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleEdit(contract)} 
                          className="p-1 hover:bg-gray-100"
                          data-testid={`edit-contract-${contract.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDelete(contract.id)} 
                          className="p-1 hover:bg-red-100 text-red-600"
                          data-testid={`delete-contract-${contract.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {/* Expanded Payment Detail */}
                  {expandedContract === contract.id && (
                    <tr>
                      <td colSpan="9" className="p-0">
                        <div className="bg-gray-50 border-t border-b border-gray-200 p-4">
                          <p className="text-xs uppercase tracking-widest text-gray-500 mb-3 font-medium">{t('monthlyPayments')}</p>
                          {contractAccruals.length > 0 ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                              {contractAccruals.map((a) => (
                                <div key={a.id} className={`p-2 border text-center text-xs ${
                                  a.status === 'paid' ? 'bg-emerald-50 border-emerald-300' :
                                  a.status === 'overdue' ? 'bg-red-50 border-red-300' :
                                  a.status === 'partial' ? 'bg-amber-50 border-amber-300' :
                                  'bg-white border-gray-200'
                                }`}>
                                  <div className="flex items-center justify-center gap-1 mb-1">
                                    {getStatusIcon(a.status)}
                                    <span className="font-medium">{a.description || a.due_date?.split('T')[0]}</span>
                                  </div>
                                  <p className="font-mono font-bold">{currency}{a.amount.toLocaleString()}</p>
                                  {a.status === 'paid' ? (
                                    <p className="text-emerald-600 mt-0.5">{language === 'tr' ? 'Ödendi' : 'Paid'}</p>
                                  ) : a.status === 'partial' ? (
                                    <p className="text-amber-600 mt-0.5">{language === 'tr' ? 'Kalan' : 'Left'}: {currency}{a.remaining_amount.toLocaleString()}</p>
                                  ) : a.status === 'overdue' ? (
                                    <p className="text-red-600 font-bold mt-0.5">{language === 'tr' ? 'Gecikmiş' : 'Overdue'}</p>
                                  ) : (
                                    <p className="text-gray-500 mt-0.5">{language === 'tr' ? 'Bekliyor' : 'Pending'}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-400 text-sm">{language === 'tr' ? 'Henüz tahakkuk oluşturulmamış. "Tahakkuk Oluştur" butonuna tıklayın.' : 'No accruals generated yet. Click "Generate Accruals".'}</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="text-center py-8 text-gray-500">
                  {t('noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {editingContract ? t('editContract') : t('addContract')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('units')} *</label>
                <Select 
                  value={formData.unit_id} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, unit_id: val }))}
                  disabled={!!editingContract}
                >
                  <SelectTrigger className="rounded-none mt-1" data-testid="contract-unit-select">
                    <SelectValue placeholder={t('selectUnit')} />
                  </SelectTrigger>
                  <SelectContent>
                    {units.filter(u => u.status === 'vacant' || u.id === formData.unit_id).map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">{t('tenants')} *</label>
                <Select 
                  value={formData.tenant_id} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, tenant_id: val }))}
                  disabled={!!editingContract}
                >
                  <SelectTrigger className="rounded-none mt-1" data-testid="contract-tenant-select">
                    <SelectValue placeholder={t('selectTenant')} />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('startDate')} *</label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  required
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('endDate')} *</label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  required
                  className="rounded-none mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('monthlyRent')} *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.monthly_rent}
                  onChange={(e) => setFormData(prev => ({ ...prev, monthly_rent: e.target.value }))}
                  required
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('dueDay')}</label>
                <Select 
                  value={formData.due_day} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, due_day: val }))}
                >
                  <SelectTrigger className="rounded-none mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(28)].map((_, i) => (
                      <SelectItem key={i+1} value={(i+1).toString()}>{i+1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('waterFee')}</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.water_fee}
                  onChange={(e) => setFormData(prev => ({ ...prev, water_fee: e.target.value }))}
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('internetFee')}</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.internet_fee}
                  onChange={(e) => setFormData(prev => ({ ...prev, internet_fee: e.target.value }))}
                  className="rounded-none mt-1"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="deposit_exists"
                  checked={formData.deposit_exists}
                  onChange={(e) => setFormData(prev => ({ ...prev, deposit_exists: e.target.checked }))}
                  className="rounded-none"
                />
                <label htmlFor="deposit_exists" className="text-sm font-medium">{t('deposit')}</label>
              </div>
              {formData.deposit_exists && (
                <div>
                  <label className="text-sm font-medium">{t('depositAmount')}</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.deposit_amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, deposit_amount: e.target.value }))}
                    className="rounded-none mt-1"
                  />
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-none">
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-black text-white rounded-none hover:bg-gray-800" data-testid="contract-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractsPage;
